import React, { useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, MessageCircle, Instagram, Check } from "lucide-react";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public canonical link that includes UTM params for later attribution.
const shareUrl = (lang) =>
  `https://npdlbr.com.br/teste-de-nivel?utm_source=share&utm_medium=quiz_result&utm_campaign=level_${lang}`;

const SHARE_COPY = {
  pt: {
    overline: "Compartilhe seu resultado",
    title: "Desafie um amigo — quantos acertam antes de você?",
    subtitle:
      "Um empurrãozinho amigo é o que falta pra alguém começar. Envie o teu resultado e o link do teste.",
    wa: (level) =>
      `Descobri que sou ${level} em francês no teste do Prof Lionel! 🇫🇷 Faz o teu (é grátis, 5 min): ${shareUrl("pt")}`,
    waLabel: "WhatsApp",
    copyLabel: "Copiar link",
    copiedLabel: "Copiado!",
    igLabel: "Instagram Stories",
    igHint: "Baixe o card e cole no Story",
    nativeLabel: "Mais opções",
    downloadLabel: "Baixar card (PNG)",
  },
  en: {
    overline: "Share your result",
    title: "Challenge a friend — can they beat you?",
    subtitle:
      "A friendly nudge is all it takes. Send them your result and the test link.",
    wa: (level) =>
      `I just found out I'm ${level} in French with Prof Lionel's test! 🇫🇷 Try yours (free, 5 min): ${shareUrl("en")}`,
    waLabel: "WhatsApp",
    copyLabel: "Copy link",
    copiedLabel: "Copied!",
    igLabel: "Instagram Stories",
    igHint: "Download the card and paste in your Story",
    nativeLabel: "More options",
    downloadLabel: "Download card (PNG)",
  },
  fr: {
    overline: "Partage ton résultat",
    title: "Défie un ami — arrive-t-il à te battre ?",
    subtitle:
      "Un petit coup de pouce, ça change tout. Envoie ton résultat et le lien du test.",
    wa: (level) =>
      `J'ai découvert que je suis ${level} en français avec le test du Prof Lionel ! 🇫🇷 Fais le tien (offert, 5 min) : ${shareUrl("fr")}`,
    waLabel: "WhatsApp",
    copyLabel: "Copier le lien",
    copiedLabel: "Copié !",
    igLabel: "Instagram Stories",
    igHint: "Télécharge la carte et colle-la dans ta Story",
    nativeLabel: "Plus d'options",
    downloadLabel: "Télécharger la carte (PNG)",
  },
};

const QuizSocialShare = ({ level, lang = "pt" }) => {
  const t = SHARE_COPY[lang] || SHARE_COPY.pt;
  const [copied, setCopied] = useState(false);

  const ogUrl = `${API}/og/level/${level}?lang=${lang}`;
  const pageUrl = shareUrl(lang);
  const waText = encodeURIComponent(t.wa(level));
  const waHref = `https://wa.me/?text=${waText}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      toast.success(t.copiedLabel);
      track("quiz_share_click", { channel: "copy_link", level, lang });
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const handleWhatsApp = () => {
    track("quiz_share_click", { channel: "whatsapp", level, lang });
    window.open(waHref, "_blank", "noopener,noreferrer");
  };

  const handleInstagram = () => {
    // On mobile we can't push directly into IG story; we open the OG card in a
    // new tab so the user can save it (long-press) and paste into their story.
    track("quiz_share_click", { channel: "instagram", level, lang });
    window.open(ogUrl, "_blank", "noopener,noreferrer");
  };

  const handleNative = async () => {
    track("quiz_share_click", { channel: "native", level, lang });
    if (navigator.share) {
      try {
        await navigator.share({
          title: t.title,
          text: t.wa(level),
          url: pageUrl,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      handleCopy();
    }
  };

  return (
    <section
      data-testid="quiz-social-share"
      className="mt-16 rounded-2xl border border-[#e5e3db] bg-white p-6 sm:p-8"
    >
      <p className="overline text-[#d9381e]">{t.overline}</p>
      <h3 className="font-display mt-3 text-2xl sm:text-3xl tracking-tight text-[#1e2a5a]">
        {t.title}
      </h3>
      <p className="mt-3 text-sm sm:text-base text-[#5c5c5c] leading-relaxed max-w-lg">
        {t.subtitle}
      </p>

      {/* Preview thumbnail so the user sees what will be shared */}
      <div className="mt-6 overflow-hidden rounded-xl border border-[#e5e3db] bg-[#1e2a5a]">
        <img
          src={ogUrl}
          alt={`Nível ${level}`}
          className="w-full h-auto block"
          loading="lazy"
          data-testid="quiz-share-preview"
        />
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleWhatsApp}
          data-testid="quiz-share-whatsapp"
          className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-medium text-white transition-transform hover:scale-[1.02]"
        >
          <MessageCircle className="h-4 w-4" strokeWidth={2} />
          {t.waLabel}
        </button>
        <button
          type="button"
          onClick={handleInstagram}
          data-testid="quiz-share-instagram"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#8134af] px-5 py-2.5 text-sm font-medium text-white transition-transform hover:scale-[1.02]"
          title={t.igHint}
        >
          <Instagram className="h-4 w-4" strokeWidth={2} />
          {t.igLabel}
        </button>
        <button
          type="button"
          onClick={handleCopy}
          data-testid="quiz-share-copy"
          className="inline-flex items-center gap-2 rounded-full border border-[#1e2a5a] px-5 py-2.5 text-sm font-medium text-[#1e2a5a] transition-colors hover:bg-[#1e2a5a] hover:text-[#f4f1ea]"
        >
          {copied ? (
            <Check className="h-4 w-4" strokeWidth={2} />
          ) : (
            <Copy className="h-4 w-4" strokeWidth={2} />
          )}
          {copied ? t.copiedLabel : t.copyLabel}
        </button>
        <button
          type="button"
          onClick={handleNative}
          data-testid="quiz-share-native"
          className="inline-flex items-center gap-2 rounded-full border border-[#e5e3db] px-5 py-2.5 text-sm text-[#5c5c5c] transition-colors hover:border-[#1e2a5a] hover:text-[#1e2a5a]"
        >
          <Share2 className="h-4 w-4" strokeWidth={2} />
          {t.nativeLabel}
        </button>
        <a
          href={ogUrl}
          download={`nivel-${level.toLowerCase()}-npdlbr.png`}
          data-testid="quiz-share-download"
          onClick={() => track("quiz_share_click", { channel: "download", level, lang })}
          className="inline-flex items-center gap-2 rounded-full border border-[#e5e3db] px-5 py-2.5 text-sm text-[#5c5c5c] transition-colors hover:border-[#1e2a5a] hover:text-[#1e2a5a]"
        >
          {t.downloadLabel}
        </a>
      </div>
    </section>
  );
};

export default QuizSocialShare;
